import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import '../styles/phone-input-override.css';
import { mvpApi } from '../services/api';
import { getQualificationResponses } from '../utils/qualificationResponses';
import { SSO_SIGNUP_STEP1_KEY } from './SsoSignupDetails';
import { useAutoFocus } from '../utils/useAutoFocus';
import LegalModal from '../components/LegalModal';
import LanguageSwitch from '../components/LanguageSwitch';
import AppHeader from '../components/AppHeader';
import { isStrictEmail, normalizeEmail } from '../utils/formInputUtils';
import './Registration.css';
import './SsoSignupFlow.css';

const RESEND_SECONDS = 30;
const PHONE_LENGTH_RULES = {
  34: { min: 9, max: 9 }, // Spain
  91: { min: 10, max: 10 }, // India
  86: { min: 11, max: 11 }, // China
  52: { min: 10, max: 10 }, // Mexico
  55: { min: 11, max: 11 }, // Brazil
  54: { min: 10, max: 11 }, // Argentina
  57: { min: 10, max: 10 }, // Colombia
  51: { min: 9, max: 9 }, // Peru
  56: { min: 9, max: 9 }, // Chile
  971: { min: 9, max: 9 }, // UAE
  966: { min: 9, max: 9 }, // Saudi Arabia
  974: { min: 8, max: 8 }, // Qatar
  965: { min: 8, max: 8 }, // Kuwait
  972: { min: 9, max: 9 }, // Israel
  962: { min: 9, max: 9 }, // Jordan
  44: { min: 10, max: 10 }, // UK
  33: { min: 9, max: 9 }, // France
  49: { min: 10, max: 11 }, // Germany
  39: { min: 9, max: 10 }, // Italy
  61: { min: 9, max: 10 }, // Australia
  212: { min: 9, max: 9 }, // Morocco
  1: { min: 10, max: 10 }, // USA
};
const MIDDLE_EAST_DIAL_CODES = new Set(['971', '966', '974', '965', '972', '962']);
const LATAM_DIAL_CODES = new Set(['52', '55', '54', '57', '51', '56']);

function readStoredEmail() {
  try {
    const u = JSON.parse(localStorage.getItem('userData') || '{}');
    return (u.email || '').trim();
  } catch {
    return '';
  }
}

function readStep1() {
  try {
    const raw = sessionStorage.getItem(SSO_SIGNUP_STEP1_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data.firstName || !data.lastName || !data.applicantType || !data.familyType) return null;
    return data;
  } catch {
    return null;
  }
}

/**
 * Step 2 of SSO signup: phone OTP, optional email, terms by continuing — matches mockup `contact.tsx`; uses profile phone OTP APIs.
 */
export default function SsoSignupContact() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [openLegal, setOpenLegal] = useState(null);
  const [step1, setStep1] = useState(() => readStep1());

  const [countryCode, setCountryCode] = useState('34');
  const [phone, setPhone] = useState('');

  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [sendLoading, setSendLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  const [email, setEmail] = useState(readStoredEmail);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [phoneValidationError, setPhoneValidationError] = useState(false);

  const phoneInputRef = useRef(null);
  const otpInputRef = useRef(null);
  const sendCodeButtonRef = useRef(null);
  const emailInputRef = useRef(null);

  useAutoFocus(phoneInputRef, { select: false });

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/', { replace: true });
      return;
    }
    const s1 = readStep1();
    if (!s1) {
      navigate('/signup/sso-details', { replace: true });
      return;
    }
    setStep1(s1);
  }, [navigate]);

  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const t = setTimeout(() => setResendTimer((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const digitsOnly = useCallback((value) => String(value || '').replace(/\D/g, ''), []);
  const normalizeNationalNumber = useCallback((rawValue, dialCode) => {
    const raw = String(rawValue || '').trim();
    const dial = digitsOnly(dialCode);
    let digits = digitsOnly(rawValue);
    if (!digits) return '';

    const hasIntlPrefix = raw.startsWith('+') || raw.startsWith('00') || (dial && digits.startsWith(`00${dial}`));
    if (dial && hasIntlPrefix) {
      if (digits.startsWith(`00${dial}`)) {
        digits = digits.slice(dial.length + 2);
      } else if (digits.startsWith(dial)) {
        digits = digits.slice(dial.length);
      }
    }
    return digits;
  }, [digitsOnly]);
  const getPhoneLengthRule = useCallback((dialCode) => {
    const cleanDial = digitsOnly(dialCode);
    if (PHONE_LENGTH_RULES[cleanDial]) return PHONE_LENGTH_RULES[cleanDial];
    if (MIDDLE_EAST_DIAL_CODES.has(cleanDial)) return { min: 8, max: 9 };
    if (LATAM_DIAL_CODES.has(cleanDial)) return { min: 9, max: 11 };
    return { min: 8, max: 11 };
  }, [digitsOnly]);

  const buildE164 = useCallback(() => {
    const dialDigits = digitsOnly(countryCode);
    const national = digitsOnly(phone);
    return `+${dialDigits}${national}`;
  }, [countryCode, phone, digitsOnly]);

  const phoneDigits = digitsOnly(phone);
  const { min: phoneMin, max: phoneMax } = getPhoneLengthRule(countryCode);
  const phoneValid = phoneDigits.length >= phoneMin && phoneDigits.length <= phoneMax;
  const canContinue = otpVerified && phoneValid;
  const hasInvalidEmail = Boolean(normalizeEmail(email) && !isStrictEmail(email));
  const isPhoneAlreadyRegisteredError = typeof error === 'string'
    && /phone number is already registered|numero de telefono ya esta registrado/i.test(error);
  const isContinueDisabled = !canContinue || submitLoading || hasInvalidEmail;

  const focusAndScroll = (target) => {
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      target.focus({ preventScroll: true });
    }, 200);
  };

  const getFirstInvalidField = () => {
    if (!phoneValid) return phoneInputRef.current;
    if (!otpVerified) return otpSent ? otpInputRef.current : sendCodeButtonRef.current;
    if (hasInvalidEmail) return emailInputRef.current;
    return null;
  };

  const handleDisabledContinueAttempt = () => {
    if (!isContinueDisabled) return;
    if (!phoneValid) setPhoneValidationError(true);
    focusAndScroll(getFirstInvalidField());
  };

  const handleSendOtp = async () => {
    if (!phoneValid) {
      setPhoneValidationError(true);
      return;
    }
    if (resendTimer > 0 || sendLoading) return;
    const e164 = buildE164();
    if (e164.length < 10) {
      setError('Please enter a valid phone number.');
      return;
    }
    setError(null);
    setSendLoading(true);
    try {
      await mvpApi.requestProfilePhoneOtp(e164);
      setOtpSent(true);
      setOtpError(false);
      setOtp('');
      setOtpVerified(false);
      setResendTimer(RESEND_SECONDS);
      setTimeout(() => otpInputRef.current?.focus(), 100);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail[0]?.msg
            : err.message || 'Failed to send code. Try again.'
      );
    } finally {
      setSendLoading(false);
    }
  };

  const handleOtpChange = async (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setOtp(digits);
    setOtpError(false);
    if (digits.length !== 6 || !otpSent || otpVerified || verifyLoading) return;
    const e164 = buildE164();
    setVerifyLoading(true);
    try {
      const resp = await mvpApi.verifyProfilePhoneOtp(e164, digits);
      const user = resp?.data?.user;
      if (user) {
        const prev = JSON.parse(localStorage.getItem('userData') || '{}');
        localStorage.setItem(
          'userData',
          JSON.stringify({
            ...prev,
            phone: user.phone,
            phone_verified: user.phone_verified,
          })
        );
      }
      setOtpVerified(true);
    } catch {
      setOtpError(true);
      setOtp('');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0 || sendLoading) return;
    setOtp('');
    setOtpVerified(false);
    setOtpError(false);
    await handleSendOtp();
  };

  const handleFinalSubmit = async () => {
    if (!canContinue || !step1 || submitLoading) return;
    const normalizedEmail = normalizeEmail(email);
    if (normalizedEmail && !isStrictEmail(normalizedEmail)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setError(null);
    setSubmitLoading(true);
    try {
      const res = await mvpApi.updatePersonalDetails({
        first_name: step1.firstName,
        last_name: step1.lastName,
        email: normalizedEmail || null,
        date_of_birth: null,
        country_of_origin: null,
        applicant_type: step1.applicantType === 'me' ? 'self' : 'others',
        application_type: step1.familyType === 'just_me' ? 'individual' : 'family',
        family_count: step1.familyType === 'with_family' ? Math.max(2, Number(step1.familyCount) || 2) : null,
        email_primary: false,
        consent_updates: true,
        qualificationResponses: getQualificationResponses() || null,
      });
      const user = res.data?.user;
      if (user) localStorage.setItem('userData', JSON.stringify(user));
      sessionStorage.removeItem(SSO_SIGNUP_STEP1_KEY);
      navigate('/es/regularizacion/success', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail[0]?.msg
            : err.message || 'Could not save. Please try again.'
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!step1) {
    return null;
  }

  return (
    <div className="reg-page">
      <AppHeader rightSlot={<LanguageSwitch />} />

      <main className="reg-main">
        <div className="reg-back-row">
          <button
            type="button"
            className="reg-back-btn"
            onClick={() => navigate('/signup/sso-details')}
            aria-label="Go back"
          >
            ←
          </button>
          <span className="reg-eyebrow">{t('ssoSignup.newAccount')}</span>
        </div>

        <div className="reg-progress">
          <div className="sso-progress-seg sso-progress-seg--fill" />
          <div className="sso-progress-seg sso-progress-seg--fill" />
          <div className="sso-progress-seg sso-progress-seg--fill" />
        </div>
        <div className="reg-step-row">
          <span>{t('ssoSignup.step3of3')}</span>
          <span className="sso-badge-almost">{t('ssoSignup.almostDone')}</span>
        </div>

        <h1 className="sso-title">
          <Trans
            i18nKey="ssoSignup.contactTitle"
            components={{ accent: <span className="sso-title-accent" /> }}
          />
        </h1>
        <p className="sso-subdesc">{t('ssoSignup.contactSubtitle')}</p>

        <div className="reg-divider" style={{ marginBottom: 20 }} />

        <div style={{ marginBottom: 4 }}>
          <label className="reg-label">{t('ssoSignup.phonePreferred')}</label>
          <div className="sso-phone-row">
            <div className="signup-country-wrap">
              <PhoneInput
                country="es"
                enableSearch
                disableSearchIcon
                value={countryCode}
                onChange={(_, countryData) => {
                  setCountryCode(countryData?.dialCode || '');
                  setPhone('');
                  setPhoneValidationError(false);
                  setOtpSent(false);
                  setOtpVerified(false);
                  setOtp('');
                }}
                inputProps={{
                  id: 'ssoCountryCode',
                  name: 'ssoCountryCode',
                  readOnly: true,
                  'aria-label': 'Country code',
                }}
                buttonClass="signup-country-button"
                containerClass="signup-country-phone"
                inputClass="signup-country-code-input"
                dropdownClass="signup-country-dropdown"
                searchClass="signup-country-search"
              />
            </div>
            <input
              ref={phoneInputRef}
              type="tel"
              className={`sso-phone-input ${phoneValidationError && !phoneValid ? 'sso-phone-input--invalid' : ''}`}
              placeholder="691 765 302"
              value={phone}
              onChange={(e) => {
                const normalized = normalizeNationalNumber(e.target.value, countryCode);
                const clampedDigits = Number.isFinite(phoneMax) ? normalized.slice(0, phoneMax) : normalized;
                setPhone(clampedDigits);
                const nextPhoneValid = clampedDigits.length >= phoneMin && clampedDigits.length <= phoneMax;
                if (nextPhoneValid) setPhoneValidationError(false);
                if (otpSent) {
                  setOtpSent(false);
                  setOtpVerified(false);
                  setOtp('');
                }
              }}
              autoFocus
              inputMode="numeric"
              autoComplete="tel-national"
            />
            {!otpVerified && (
              <button
                ref={sendCodeButtonRef}
                type="button"
                className="sso-send-code-btn"
                disabled={!phoneValid || sendLoading || (otpSent && resendTimer > 0)}
                onClick={handleSendOtp}
              >
              {sendLoading ? '…' : otpSent ? t('ssoSignup.resend') : t('ssoSignup.sendCode')}
              </button>
            )}
            {otpVerified && (
              <div className="sso-verified-pill">
                <span>
                  <i className="ri-check-line" aria-hidden />
                </span>
                <span>{t('ssoSignup.verified')}</span>
              </div>
            )}
          </div>
          {isPhoneAlreadyRegisteredError && <p className="reg-error">{error}</p>}
          {!otpSent && (
            <p className="reg-help" style={{ marginTop: 4 }}>
              {t('ssoSignup.contactBy')}
            </p>
          )}
        </div>

        {otpSent && !otpVerified && (
          <div className="sso-otp-block">
            <label className="reg-label">{t('ssoSignup.enterCode')}</label>
            <input
              ref={otpInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="sso-otp-input"
              value={otp}
              onChange={(e) => handleOtpChange(e.target.value)}
              placeholder="——————"
              disabled={verifyLoading}
            />
            {otpError && <p className="sso-otp-error">{t('ssoSignup.incorrectCode')}</p>}
            <div className="sso-resend-row">
              {resendTimer <= 0 && (
                <button
                  type="button"
                  className="sso-resend-btn"
                  onClick={handleResend}
                  disabled={sendLoading}
                >
                  {t('ssoSignup.resendCode')}
                </button>
              )}
              {resendTimer > 0 && (
                <span className="sso-resend-timer">{t('ssoSignup.inSeconds', { seconds: resendTimer })}</span>
              )}
            </div>
          </div>
        )}

        {otpVerified && <div style={{ marginBottom: 16 }} />}

        <div className="reg-divider" style={{ marginBottom: 20 }} />

        <div style={{ marginBottom: 20 }}>
          <label className="reg-label">
            {t('ssoSignup.verifyEmailOptional')}{' '}
            <span className="reg-label-optional" style={{ textTransform: 'none', letterSpacing: 'normal' }}>
              {t('ssoSignup.optional')}
            </span>
          </label>
          <input
            ref={emailInputRef}
            type="email"
            className={`reg-input ${hasInvalidEmail ? 'reg-input--invalid' : ''}`}
            placeholder={t('ssoSignup.placeholderEmail')}
            value={email}
            onChange={(e) => {
              const next = e.target.value;
              setEmail(next);
              const normalized = normalizeEmail(next);
              setEmailError(normalized && !isStrictEmail(normalized) ? 'Please enter a valid email address.' : '');
            }}
            onPaste={(e) => {
              const pasted = e.clipboardData?.getData('text') || '';
              const normalized = normalizeEmail(`${email}${pasted}`);
              setEmailError(normalized && !isStrictEmail(normalized) ? 'Please enter a valid email address.' : '');
            }}
          />
          {emailError ? <p className="reg-error">{emailError}</p> : null}
          <p className="reg-help">{t('ssoSignup.updatesOrDocs')}</p>
        </div>

        <div className="reg-divider" style={{ marginBottom: 20 }} />

        <p className="sso-footer-micro" style={{ marginTop: 16 }}>
          {t('ssoSignup.onlyCaseContact')}
        </p>

        {error && !isPhoneAlreadyRegisteredError && <p className="reg-error">{error}</p>}

        <div onClick={handleDisabledContinueAttempt}>
          <button
            type="button"
            className="btn-primary"
            disabled={isContinueDisabled}
            onClick={handleFinalSubmit}
          >
            {submitLoading ? t('ssoSignup.saving') : t('ssoSignup.continueNextSteps')}
          </button>
        </div>

        <p className="reg-details-terms sso-contact-continue-terms">
          <Trans
            i18nKey="ssoSignup.detailsContinueTerms"
            components={{
              terms: (
                <button type="button" className="reg-details-terms-link" onClick={() => setOpenLegal('terms')} />
              ),
              privacy: (
                <button type="button" className="reg-details-terms-link" onClick={() => setOpenLegal('privacy')} />
              ),
              dpa: <button type="button" className="reg-details-terms-link" onClick={() => setOpenLegal('data')} />,
            }}
          />
        </p>
      </main>

      <LegalModal type={openLegal || 'terms'} open={openLegal !== null} onClose={() => setOpenLegal(null)} />
    </div>
  );
}
